// frontend/hooks/useProducts.js

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as productService from '../store/services/productService';
import { createProductWithAssetsWorkflow } from '../store/services/productService';
import { useInfiniteQuery } from '@tanstack/react-query';

// Custom hook to fetch all products
// export const useAllProducts = () => {
//   return useQuery({
//     queryKey: ['products'],
//     queryFn: productService.getAllProducts,
//   });
// };
// src/hooks/useProducts.js — update useAllProducts signature only, everything else unchanged
export const useAllProducts = (options = {}) => {
  return useQuery({
    queryKey: ['products'],
    queryFn: productService.getAllProducts,
    enabled: options.enabled ?? true,
  });
};

// Dedicated infinite query hook now utilizing the clean service boundary pipeline
export const useInfiniteProducts = () => {
  return useInfiniteQuery({
    queryKey: ['products', 'infinite'],
    queryFn: async ({ pageParam = 1 }) => {
      // Executes server-side range queries directly
      return await productService.getInfiniteProducts({ pageParam, limit: 20 });
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
  });
};
// Custom hook to fetch a single product by ID
export const useProduct = (id) => {
  return useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getProductById(id),
    // This query will not run if the ID is null or undefined
    enabled: !!id,
  });
};

// Custom hook to create a new product

export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ payload, uploadBatch, userId, shopId }) => {
        return await createProductWithAssetsWorkflow({ payload, uploadBatch, userId, shopId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};
// Custom hook to update an existing product
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => productService.updateProduct(payload.id, payload),
    onSuccess: (data) => {
      // Invalidate the 'products' list and the specific product's detail query
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', data.id] });
    },
  });
};

// Custom hook to delete a product
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: productService.deleteProduct,
    onSuccess: () => {
      // Invalidate the 'products' list to remove the deleted item from the cache
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};


// New: Custom hook to search products by query
export const useSearchProducts = (query) => {
  return useQuery({
    queryKey: ['products', 'search', query],
    queryFn: () => productService.searchProducts(query),
    enabled: !!query,
  });
};

// NEW: Hook to get a paginated list of products
export const useGetPaginatedProducts = (page, limit) => {
    return useQuery({
        queryKey: ['products', 'paginated', page, limit],
        queryFn: () => productService.getPaginatedProducts(page, limit),
    });
};

// NEW: Hook to get products by a list of IDs
export const useGetProductsByIds = (ids) => {
    return useQuery({
        queryKey: ['products', 'byIds', ids],
        queryFn: () => productService.getProductsByIds(ids),
        enabled: ids && ids.length > 0,
    });
};

// NEW: Hook to update a product's recommended list
export const useUpdateProductRecommendedList = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ productId, recommendedIds }) => 
            productService.updateProductRecommendedList(productId, recommendedIds),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['product', data.id] });
            queryClient.invalidateQueries({ queryKey: ['products', 'byIds'] });
        },
    });
};

// NEW: Hook to update a product's accessories list
export const useUpdateProductAccessoriesList = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ productId, accessoriesIds }) => 
            productService.updateProductAccessoriesList(productId, accessoriesIds),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['product', data.id] });
            queryClient.invalidateQueries({ queryKey: ['products', 'byIds'] });
        },
    });
};

// NEW: Hook to search for a single product by name
export const useSearchProductByName = (query) => {
    return useQuery({
        queryKey: ['products', 'searchByName', query],
        queryFn: () => productService.searchProductByName(query),
        enabled: !!query,
    });
};

// NEW: Hook to get all products within a specific series
export const useGetProductsBySeries = (seriesId) => {
    return useQuery({
        queryKey: ['products', 'series', seriesId],
        queryFn: () => productService.getProductsBySeries(seriesId),
        enabled: !!seriesId, // Only run the query if a seriesId is provided
    });
};
// frontend/hooks/useProducts.js


// NEW: Hook to search products by name and return a list of matches
export const useSearchProductsByName = (query) => {
    return useQuery({
        queryKey: ['products', 'searchByName', query],
        queryFn: () => productService.searchProductsByName(query),
        enabled: !!query, // The query will only run if a search query exists
    });
};


// NEW: Hook to update a product's specs_carousel
export const useUpdateProductSpecsCarousel = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ productId, specsCarouselData }) =>
            productService.updateProductSpecsCarousel(productId, specsCarouselData),
        onSuccess: (data) => {
            // Invalidate the specific product to refetch the updated data
            queryClient.invalidateQueries({ queryKey: ['product', data.id] });
            queryClient.invalidateQueries({ queryKey: ['products'] });
        },
    });
};

// NEW: Hook to get products with an empty specs_carousel for staff
export const useGetProductsWithEmptySpecsCarousel = (page, limit) => {
    return useQuery({
        queryKey: ['products', 'emptySpecsCarousel', page, limit],
        queryFn: () => productService.getProductsWithEmptySpecsCarousel(page, limit),
    });
};
///////////////////////////////////////////////////////
///////////////////////////////////////////////////////

// useProducts.js (FIXED)

// NEW: Hook to update a product's specs_carousel
export const updateProductSpecsData = () => { // Renamed for clarity on what it updates
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ productId, specsData }) =>
            productService.updateProductSpecsData(productId, specsData),
        onSuccess: (data) => {
            // ✅ FIX: Add a null/undefined check before attempting to access .id
            if (data?.id) { 
                // Invalidate the specific product to refetch the updated data
                queryClient.invalidateQueries({ queryKey: ['product', data.id] });
                queryClient.invalidateQueries({ queryKey: ['products'] });
            } else {
                 console.warn("Update succeeded but returned no valid product data for cache invalidation.");
            }
        },
    });
};

// NEW: Hook to get products with an empty specs_carousel for staff
export const useGetProductsWithEmptySpecsData = (page, limit) => {
    return useQuery({
        queryKey: ['products', 'emptySpecsData', page, limit],
        queryFn: () => productService.getProductsWithEmptySpecsData(page, limit),
    });
};



// NEW: Hook to get a paginated and filtered list of products by category name
export const useGetPaginatedProductsByCategory = (categoryName, page, limit = 20) => {
    return useQuery({
        // The query key includes all dependencies for correct cache separation
        queryKey: ['products', 'paginated', 'category', categoryName, page, limit],
        queryFn: () => productService.getPaginatedProductsByCategory(categoryName, page, limit),
        // Only run the query if a categoryName is provided and page is a valid number
        enabled: !!categoryName && typeof page === 'number',
        // Optional: Keep previous data when fetching new pages (for infinite scrolling effect)
        keepPreviousData: true, 
    });
};

